import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { PIPELINE } from '../data/roadmap';
import RoadmapTimeline from '../components/RoadmapTimeline';

function HomePage() {
  const { themeName } = useTheme();
  const { user } = useAuth();
  const isDark = themeName === 'dark';

  const betaBanner = {
    ...betaBannerStyle,
    background: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.22)',
    border:     isDark ? '1px solid rgba(245,158,11,0.30)' : '1px solid rgba(180,83,9,0.55)',
    color:      isDark ? '#fcd34d' : '#78350f',
  };

  const pageBg = isDark
    ? 'radial-gradient(ellipse 70% 55% at 10% 30%, rgba(99,102,241,0.14) 0%, transparent 60%), radial-gradient(ellipse 55% 45% at 88% 12%, rgba(139,92,246,0.11) 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 55% 95%, rgba(99,102,241,0.08) 0%, transparent 55%)'
    : 'radial-gradient(ellipse 70% 55% at 10% 30%, rgba(99,102,241,0.09) 0%, transparent 60%), radial-gradient(ellipse 55% 45% at 88% 12%, rgba(139,92,246,0.07) 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 55% 95%, rgba(99,102,241,0.05) 0%, transparent 55%)';

  return (
    <div style={user ? pageStyleWide : pageStyle}>
      {/* Full-viewport background gradient */}
      <div style={{ position: 'fixed', inset: 0, zIndex: -1, background: pageBg, pointerEvents: 'none' }} aria-hidden="true" />

      {/* ── Beta notice ───────────────────────────────────────────── */}
      <div style={betaBanner}>
        <span style={betaIconStyle}>✨</span>
        <span>
          <strong>Early beta</strong> — Inverow is under active development.
          New features and improvements ship regularly, so expect things to
          keep evolving — check back often to see what's new.
        </span>
      </div>

      {/* ── Hero (signed-out) or Dashboard cards (signed-in) ──────── */}
      {user ? (
        <section style={dashStyle}>
          <div style={cardsGridStyle}>
            <DashCard to="/portfolios" title="Portfolios" icon={<PortfoliosIcon />} />
            <DashCard to="/stocks"     title="Stocks"     icon={<DashStocksIcon />} />
            <DashCard to="/crypto"     title="Crypto"     icon={<DashCryptoIcon />} />
            <DashCard to="/profile"    title="Profile"    icon={<ProfileIcon />} />
            <DashCard to="/settings"   title="Settings"   icon={<SettingsIcon />} />
          </div>
        </section>
      ) : (
        <>
          <section style={heroStyle}>
            <div style={heroInnerStyle}>
              <div style={heroBadgeStyle}>Investor Toolkit</div>
              <h1 style={heroTitleStyle}>Track and manage<br />your investments</h1>
              <p style={heroDescStyle}>
                Inverow is a personal portfolio tracker built for active and passive investors.
                Monitor holdings in real time, analyze performance across multiple currencies,
                and explore live markets — with more modules on the way.
              </p>
              <div style={heroActionsStyle}>
                <Link to="/register"   style={heroPrimaryBtn}>Get started — it's free</Link>
                <Link to="/portfolios" style={heroSecondaryBtn}>See a demo portfolio</Link>
              </div>
            </div>
          </section>

          {/* ── Feature cards — what guests can do right now ── */}
          <section style={featuresSectionStyle}>
            <h2 style={featuresTitleStyle}>Explore what you can do</h2>
            <div style={featuresGridStyle}>
              <FeatureCard
                to="/portfolios"
                icon={<FeaturePortfoliosIcon />}
                title="Portfolios"
                desc="Track holdings, P/L, dividends, allocation and XIRR across multiple currencies. Try the live demo without signing up."
              />
              <FeatureCard
                to="/stocks"
                icon={<StocksIcon />}
                title="Stocks"
                desc="Live S&P 500 heatmap, top movers, popular tickers and a market Fear & Greed index — no account required."
              />
              <FeatureCard
                to="/crypto"
                icon={<CryptoIcon />}
                title="Crypto"
                desc="CoinGecko top-200 heatmap, daily gainers and losers, popular coins and the crypto sentiment gauge."
              />
            </div>
          </section>
        </>
      )}

      {/* ── Pipeline / Roadmap ────────────────────────────────────── */}
      <section style={roadmapSectionStyle}>
        <h2 style={roadmapTitleStyle}>Roadmap</h2>
        <p style={roadmapSubStyle}>What's built, what's being worked on, and what's coming next.</p>

        <RoadmapTimeline phases={PIPELINE} />
      </section>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────────────────────── */
const pageStyle = { maxWidth: '860px', margin: '0 auto', padding: '0 0 60px' };
const pageStyleWide = { width: '100%', padding: '0 24px 60px', boxSizing: 'border-box' };

const heroStyle = { textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 0 40px' };
const heroInnerStyle = { display: 'grid', gap: '20px', justifyItems: 'center' };
const heroBadgeStyle = { display: 'inline-block', padding: '6px 14px', borderRadius: '999px', background: 'rgba(129,140,248,0.14)', color: '#a5b4fc', border: '1px solid rgba(129,140,248,0.28)', fontSize: '13px', fontWeight: 700 };
const heroTitleStyle = { margin: 0, fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.15 };
const heroDescStyle = { maxWidth: '560px', margin: 0, color: 'var(--text-secondary)', lineHeight: 1.65, fontSize: '16px' };
const heroActionsStyle = { display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '4px' };
const heroPrimaryBtn = { display: 'inline-block', padding: '14px 28px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-strong) 100%)', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '15px', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' };
const heroSecondaryBtn = { display: 'inline-block', padding: '14px 28px', borderRadius: '12px', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 700, fontSize: '15px' };

/* ── Feature cards (signed-out) ── */
const featuresSectionStyle = { padding: '0 0 56px' };
const featuresTitleStyle = { margin: '0 0 24px', fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', textAlign: 'center' };
const featuresGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' };
const featureCardStyle = {
  display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center',
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '18px',
  padding: '28px 24px', textDecoration: 'none', color: 'var(--text-primary)', boxShadow: 'var(--shadow)',
  transition: 'transform 0.12s ease, border-color 0.12s ease',
};
// Fixed-height icon row so titles align on the same line across all cards
const featureIconStyle = { color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '48px' };
const featureCardTitleStyle = { fontSize: '18px', fontWeight: 800, textAlign: 'center' };
const featureCardDescStyle = { fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0, textAlign: 'center' };

const roadmapSectionStyle = { padding: '0 0 40px' };
const roadmapTitleStyle = { margin: '0 0 8px 0', fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)' };
const roadmapSubStyle = { margin: '0 0 36px 0', color: 'var(--text-secondary)', fontSize: '14px' };

const betaBannerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '13px 18px',
  borderRadius: '14px',
  background: 'rgba(245,158,11,0.10)',
  border: '1px solid rgba(245,158,11,0.30)',
  color: '#fcd34d',
  fontSize: '14px',
  lineHeight: 1.55,
  marginBottom: '0',
};

const betaIconStyle = {
  fontSize: '16px',
  flexShrink: 0,
  marginTop: '1px',
};

function DashCard({ to, title, icon }) {
  return (
    <Link to={to} style={dashCardStyle}>
      <div style={dashCardIconStyle}>{icon}</div>
      <div style={dashCardTitleStyle}>{title}</div>
    </Link>
  );
}

function FeatureCard({ to, icon, title, desc }) {
  return (
    <Link to={to} style={featureCardStyle}>
      <div style={featureIconStyle}>{icon}</div>
      <div style={featureCardTitleStyle}>{title}</div>
      <p style={featureCardDescStyle}>{desc}</p>
    </Link>
  );
}

function FeaturePortfoliosIcon() {
  return (
    <svg viewBox="0 0 64 64" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="18" width="52" height="38" rx="5" />
      <path d="M22 18V12a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v6" />
      <path d="M12 32l8 6 10-10 10 8 12-12" />
    </svg>
  );
}
function StocksIcon() {
  return (
    <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 34 18 22 26 30 42 14" />
      <polyline points="30 14 42 14 42 26" />
    </svg>
  );
}
function DashStocksIcon() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="8 46 24 30 34 40 56 18" />
      <polyline points="40 18 56 18 56 34" />
    </svg>
  );
}
function DashCryptoIcon() {
  // Clean Bitcoin ₿ glyph inside a coin
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="32" r="23" />
      <path d="M26 20v24" />
      <path d="M26 20h9a6 6 0 0 1 0 12h-9" />
      <path d="M26 32h10.5a6 6 0 0 1 0 12H26" />
      <line x1="30" y1="16" x2="30" y2="20" />
      <line x1="36" y1="16" x2="36" y2="20" />
      <line x1="30" y1="44" x2="30" y2="48" />
      <line x1="36" y1="44" x2="36" y2="48" />
    </svg>
  );
}
function CryptoIcon() {
  // Bitcoin ₿ glyph in a coin — matches the dashboard + sidebar crypto icon
  return (
    <svg viewBox="0 0 64 64" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="32" r="23" />
      <path d="M26 20v24" />
      <path d="M26 20h9a6 6 0 0 1 0 12h-9" />
      <path d="M26 32h10.5a6 6 0 0 1 0 12H26" />
      <line x1="30" y1="16" x2="30" y2="20" />
      <line x1="36" y1="16" x2="36" y2="20" />
      <line x1="30" y1="44" x2="30" y2="48" />
      <line x1="36" y1="44" x2="36" y2="48" />
    </svg>
  );
}

function PortfoliosIcon() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="18" width="52" height="38" rx="5" />
      <path d="M22 18V12a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v6" />
      <path d="M12 32l8 6 10-10 10 8 12-12" />
    </svg>
  );
}
function ProfileIcon() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="22" r="10" />
      <path d="M10 56c0-12 10-18 22-18s22 6 22 18" />
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="32" cy="32" r="8" />
      <path d="M51.7 39a4.4 4.4 0 0 0 .9 4.85l.16.16a5.33 5.33 0 1 1-7.54 7.54l-.16-.16a4.4 4.4 0 0 0-4.85-.9 4.4 4.4 0 0 0-2.67 4v.45a5.33 5.33 0 1 1-10.67 0v-.24a4.4 4.4 0 0 0-2.88-4.03 4.4 4.4 0 0 0-4.85.9l-.16.16a5.33 5.33 0 1 1-7.54-7.54l.16-.16a4.4 4.4 0 0 0 .9-4.85 4.4 4.4 0 0 0-4-2.67H8a5.33 5.33 0 1 1 0-10.67h.24A4.4 4.4 0 0 0 12.27 25a4.4 4.4 0 0 0-.9-4.85l-.16-.16a5.33 5.33 0 1 1 7.54-7.54l.16.16a4.4 4.4 0 0 0 4.85.9H24a4.4 4.4 0 0 0 2.67-4V9a5.33 5.33 0 1 1 10.67 0v.24a4.4 4.4 0 0 0 2.66 4 4.4 4.4 0 0 0 4.85-.9l.16-.16a5.33 5.33 0 1 1 7.54 7.54l-.16.16a4.4 4.4 0 0 0-.9 4.85V25a4.4 4.4 0 0 0 4 2.67h.45a5.33 5.33 0 1 1 0 10.67H55.7a4.4 4.4 0 0 0-4 2.66z" />
    </svg>
  );
}

const dashStyle = { margin: '12px 0 24px', padding: 0 };
const cardsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' };
const dashCardStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: '18px', minHeight: '200px',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: '18px', padding: '28px 26px', textDecoration: 'none',
  color: 'var(--text-primary)', boxShadow: 'var(--shadow)',
  transition: 'transform 0.12s ease, border-color 0.12s ease',
};
const dashCardIconStyle = { color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const dashCardTitleStyle = { fontSize: '22px', fontWeight: 800 };

export default HomePage;
